import { apiFetch } from '../../../utils/api';

/**
 * Toggle super admin status for a user
 * @param {string} userId - The user ID
 * @param {string} targetUserName - The user's name for confirmation
 * @param {Function} setProcessingAction - Setter for processing action state
 * @param {Function} fetchUsers - Function to refresh users
 * @returns {Promise<void>}
 */
export const handleToggleAdmin = async (userId, targetUserName, setProcessingAction, fetchUsers) => {
    if (!await confirm(`Are you sure you want to change the Super Admin status for ${targetUserName}?`)) return;
    setProcessingAction(userId);
    try {
        const res = await apiFetch(`/api/auth/admin/users/${userId}/toggle-admin`, { method: 'POST' });
        if (res.ok) {
            await fetchUsers();
        } else {
            const errData = await res.json();
            alert(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to change admin status.');
        }
    } catch (err) {
        console.error('Error toggling admin', err);
    } finally {
        setProcessingAction(null);
    }
};

/**
 * Delete a user
 * @param {string} userId - The user ID
 * @param {string} email - The user's email for confirmation
 * @param {Function} setProcessingAction - Setter for processing action state
 * @param {Function} fetchUsers - Function to refresh users
 * @param {Function} fetchOrganizations - Function to refresh organizations
 * @param {string|null} selectedId - Currently selected entity ID
 * @param {Function} setSelectedId - Setter for selected ID
 * @returns {Promise<void>}
 */
export const handleDeleteUser = async (userId, email, setProcessingAction, fetchUsers, fetchOrganizations, selectedId, setSelectedId) => {
    if (!await confirm(`CRITICAL: Are you sure you want to permanently delete the user account for ${email}? This cannot be undone.`)) return;
    setProcessingAction(userId);
    try {
        const res = await apiFetch(`/api/auth/admin/users/${userId}`, { method: 'DELETE' });
        if (res.ok) {
            await fetchUsers();
            await fetchOrganizations();
            if (selectedId === userId) setSelectedId(null);
        } else {
            const errData = await res.json();
            alert(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to delete user.');
        }
    } catch (err) {
        console.error('Error deleting user', err);
    } finally {
        setProcessingAction(null);
    }
};

/**
 * Create a new organization
 * @param {Event} e - Form event
 * @param {Object} newOrganization - Organization data { id, name, rootAdminUserId }
 * @param {Function} setNewOrganization - Setter for new organization
 * @param {Function} setOrganizationError - Setter for organization error
 * @param {Function} setOrganizationSaving - Setter for organization saving state
 * @param {Function} setIsCreateOrganizationOpen - Setter for create modal open state
 * @param {Function} setOwnerSearch - Setter for owner search
 * @param {Function} fetchOrganizations - Function to refresh organizations
 * @param {Function} fetchUsers - Function to refresh users
 * @returns {Promise<void>}
 */
export const handleCreateOrganization = async (e, newOrganization, setNewOrganization, setOrganizationError, setOrganizationSaving, setIsCreateOrganizationOpen, setOwnerSearch, fetchOrganizations, fetchUsers) => {
    e.preventDefault();
    setOrganizationError('');
    setOrganizationSaving(true);
    try {
        const res = await apiFetch('/api/auth/admin/organizations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newOrganization)
        });
        if (res.ok) {
            setNewOrganization({ id: '', name: '', rootAdminUserId: '' });
            setIsCreateOrganizationOpen(false);
            await fetchOrganizations();
            await fetchUsers();
        } else {
            const errData = await res.json();
            setOrganizationError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to create organization.');
        }
    } catch (err) {
        setOrganizationError('Connection error while creating organization.');
    } finally {
        setOrganizationSaving(false);
    }
};

/**
 * Delete an organization
 * @param {string} organizationId - The organization ID
 * @param {string} organizationName - The organization name for confirmation
 * @param {Function} setProcessingAction - Setter for processing action state
 * @param {Function} fetchOrganizations - Function to refresh organizations
 * @param {Function} fetchUsers - Function to refresh users
 * @param {string|null} selectedId - Currently selected entity ID
 * @param {Function} setSelectedId - Setter for selected ID
 * @returns {Promise<void>}
 */
export const handleDeleteOrganization = async (organizationId, organizationName, setProcessingAction, fetchOrganizations, fetchUsers, selectedId, setSelectedId) => {
    if (!await confirm(`CRITICAL WARNING: Are you sure you want to permanently delete organization "${organizationName}" (${organizationId})?\nAll associations will be wiped out. This action cannot be undone.`)) return;
    setProcessingAction(organizationId);
    try {
        const res = await apiFetch(`/api/auth/admin/organizations/${organizationId}`, { method: 'DELETE' });
        if (res.ok) {
            await fetchOrganizations();
            await fetchUsers();
            if (selectedId === organizationId) setSelectedId(null);
        } else {
            const errData = await res.json();
            alert(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to delete organization.');
        }
    } catch (err) {
        console.error('Error deleting organization', err);
    } finally {
        setProcessingAction(null);
    }
};

/**
 * Start the assign root admin workflow
 * @param {Object} organization - The organization to assign root admin to
 * @param {Function} setAssignRootAdminOrganization - Setter for root admin organization
 * @param {Function} setAssignRootAdminUserId - Setter for root admin user ID
 * @param {Function} setAssignRootAdminSearch - Setter for root admin search
 * @param {Function} setAssignRootAdminError - Setter for root admin error
 * @param {Function} setAssignRootAdminSaving - Setter for root admin saving state
 * @param {Function} setIsAssignRootAdminOpen - Setter for assign modal open state
 * @param {Array} users - Array of user objects
 * @returns {void}
 */
export const handleStartAssignRootAdmin = (organization, setAssignRootAdminOrganization, setAssignRootAdminUserId, setAssignRootAdminSearch, setAssignRootAdminError, setAssignRootAdminSaving, setIsAssignRootAdminOpen, users) => {
    setAssignRootAdminOrganization(organization);
    setAssignRootAdminUserId(organization.rootAdminUserId || '');
    const currentOwner = users.find(u => u.id === organization.rootAdminUserId);
    setAssignRootAdminSearch(currentOwner ? (currentOwner.fullName ? `${currentOwner.fullName} (${currentOwner.email})` : currentOwner.email) : '');
    setAssignRootAdminError('');
    setAssignRootAdminSaving(false);
    setIsAssignRootAdminOpen(true);
};

/**
 * Assign root administrator to an organization
 * @param {Event} e - Form event
 * @param {Object} assignRootAdminOrganization - The organization to assign to
 * @param {string} assignRootAdminUserId - The user ID to assign as root admin
 * @param {Function} setAssignRootAdminError - Setter for root admin error
 * @param {Function} setAssignRootAdminSaving - Setter for root admin saving state
 * @param {Function} setIsAssignRootAdminOpen - Setter for assign modal open state
 * @param {Function} fetchOrganizations - Function to refresh organizations
 * @param {Function} fetchUsers - Function to refresh users
 * @returns {Promise<void>}
 */
export const handleAssignRootAdmin = async (e, assignRootAdminOrganization, assignRootAdminUserId, setAssignRootAdminError, setAssignRootAdminSaving, setIsAssignRootAdminOpen, fetchOrganizations, fetchUsers) => {
    e.preventDefault();
    setAssignRootAdminError('');
    setAssignRootAdminSaving(true);
    try {
        const res = await apiFetch(`/api/auth/admin/organizations/${assignRootAdminOrganization.id}/assign-root-admin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: assignRootAdminUserId })
        });
        if (res.ok) {
            setIsAssignRootAdminOpen(false);
            await fetchOrganizations();
            await fetchUsers();
        } else {
            const errData = await res.json();
            setAssignRootAdminError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to assign root administrator.');
        }
    } catch (err) {
        setAssignRootAdminError('Connection error while assigning root administrator.');
    } finally {
        setAssignRootAdminSaving(false);
    }
};


